import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Alert, Box, Grid, Typography } from '@mui/material';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import DatePickerCustom from '../../components/DatePicker/DatePickerCustom';
import GlobalSelectFieldWithoutFormikNew from '../../components/mui/GlobalSelectFieldWithoutFormikNew';
import GlobalTableNoPagination from '../../components/CustomTableWithPagination/GlobalTableNoPagination';
import { targetManagementTableHeader } from './utils/targetManagementTableHeader';
import SetTargetDialog from './components/SetTargetDialog';
import BulkTargetUpload from './components/BulkTargetUpload';
import { getAssignableUsers } from '../../redux/features/targetManagementSlice';

const ALL_OPTION = { id: -1, value: 'All' };

// Targets can only be set in the first 5 days of the CURRENT month - same rule the existing
// OGD SOGO->OGA target dialog already enforces
// (views/ogd/myworkspace/TargetOrSlots/AddOrUpdateTarget.jsx). Finance is exempt from both this
// and the current-month restriction (server-enforced too, see target_slot_fill.controller.js).
const isWithinTargetWindow = () => {
  const day = dayjs().date();
  return day >= 1 && day <= 5;
};

const ROLE_COPY = {
  finance: 'You are in Finance, so you can set a target for anyone in the company, for any month.',
  manager: 'You can set targets for your own active team (direct and indirect reports).',
  none: "You don't currently have any active team members to set targets for.",
};

const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString('en-IN')}`;

export default function SetCroTargetsLanding() {
  const dispatch = useDispatch();
  const {
    data,
    role,
    totalTarget,
    totalSlot,
    totalUsers,
    truncated,
    departments,
    wings,
    loading,
  } = useSelector((state) => state.targetManagement.assignableUsers);

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM-01'));
  const [selectedDept, setSelectedDept] = useState(ALL_OPTION);
  const [selectedWing, setSelectedWing] = useState(ALL_OPTION);
  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: ['name'],
    hiddenColumns: [],
  });

  const isCurrentMonthSelected = selectedMonth === dayjs().format('YYYY-MM-01');
  const isFinance = role === 'finance';
  // Only Finance can touch a non-current month at all; everyone else is further limited to
  // the 1st-5th window on the current month.
  const withinWindow = isFinance || (isCurrentMonthSelected && isWithinTargetWindow());

  const deptOptions = useMemo(
    () => [ALL_OPTION, ...departments.map((d) => ({ id: d.deptId, value: d.deptName }))],
    [departments],
  );
  const wingOptions = useMemo(() => {
    const scoped = selectedDept.id === -1 ? wings : wings.filter((w) => w.deptId === selectedDept.id);
    return [ALL_OPTION, ...scoped.map((w) => ({ id: w.wingId, value: w.wingName }))];
  }, [wings, selectedDept]);

  // Reuse the table's own built-in search box (bottom of GlobalTableNoPagination's toolbar)
  // instead of a second one - tableState.search drives the actual server-side query (needed
  // so Finance can find someone outside the first 500 rows, which a client-only filter over
  // already-loaded rows never could), and the table's own client-side filter on top of that
  // is a harmless no-op since the results already match.
  const fetchData = useCallback(() => {
    dispatch(getAssignableUsers({
      search: tableState.search,
      month: selectedMonth,
      departmentId: selectedDept.id === -1 ? null : selectedDept.id,
      wingId: selectedWing.id === -1 ? null : selectedWing.id,
    }));
  }, [dispatch, tableState.search, selectedMonth, selectedDept.id, selectedWing.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Picking a department that no longer contains the currently-selected wing should reset
  // the wing filter, instead of silently filtering on a wing that's no longer visible.
  useEffect(() => {
    if (selectedWing.id !== -1 && !wingOptions.some((w) => w.id === selectedWing.id)) {
      setSelectedWing(ALL_OPTION);
    }
  }, [wingOptions, selectedWing.id]);

  const renderAction = useCallback(
    ({ row }) => <SetTargetDialog user={row} month={selectedMonth} onSaved={fetchData} disabled={!withinWindow} />,
    [fetchData, withinWindow, selectedMonth],
  );

  const headers = useMemo(() => targetManagementTableHeader(renderAction, data), [renderAction, data]);

  return (
    <GlobalWrapper title="Set CRO Targets">
      <Box mb={1} sx={{ backgroundColor: 'white', borderRadius: '10px', p: 1.5 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6} md={3}>
            <DatePickerCustom
              label="Month"
              value={dayjs(selectedMonth)}
              setValue={(date) => setSelectedMonth(date.format('YYYY-MM-01'))}
              viewDetails={['year', 'month']}
              fullWidth
              dateDisplayFormat="MM-YYYY"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'value' }}
              data={deptOptions}
              label="Department"
              fullWidth
              selectedDetails={selectedDept}
              setChange={setSelectedDept}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'value' }}
              data={wingOptions}
              label="Wing"
              fullWidth
              selectedDetails={selectedWing}
              setChange={setSelectedWing}
            />
          </Grid>
          <Grid item xs={6} md={3} sx={{ display: 'flex', justifyContent: { md: 'flex-end' } }}>
            <BulkTargetUpload rows={data} month={selectedMonth} disabled={!withinWindow || !data.length} onSaved={fetchData} />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={1.5} mb={1}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: 'white' }}>
            <Typography variant="caption" color="text.secondary">Total Target</Typography>
            <Typography variant="h6" fontWeight={700}>{formatCurrency(totalTarget)}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: 'white' }}>
            <Typography variant="caption" color="text.secondary">Total Slot</Typography>
            <Typography variant="h6" fontWeight={700}>{Number(totalSlot || 0).toLocaleString('en-IN')}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0', backgroundColor: 'white' }}>
            <Typography variant="caption" color="text.secondary">People</Typography>
            <Typography variant="h6" fontWeight={700}>{totalUsers}</Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: '#1E293B', color: '#fff' }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Viewing</Typography>
            <Typography variant="h6" fontWeight={700}>{dayjs(selectedMonth).format('MMMM YYYY')}</Typography>
          </Box>
        </Grid>
      </Grid>

      {role && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {ROLE_COPY[role]}
        </Typography>
      )}

      {truncated && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Showing the first 500 matches - narrow your search, department, or wing filter to see everyone and get
          accurate totals.
        </Alert>
      )}

      {!withinWindow && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {isCurrentMonthSelected
            ? 'Targets can only be set on the 1st–5th of the month. You can still view current targets below.'
            : 'Only Finance can set targets for a month other than the current one. You can still view this month\'s targets below.'}
        </Alert>
      )}

      {role === 'none' && !loading && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {ROLE_COPY.none}
        </Alert>
      )}

      <GlobalTableNoPagination
        headers={headers}
        rows={data}
        loading={loading}
        tableState={tableState}
        setTableState={setTableState}
        rowUniqueId="userID"
        addSerialNo={false}
      />
    </GlobalWrapper>
  );
}
